import { getByID } from '@adornis/baseql/operations/mongo.js';
import { countingTableView } from '@nsc/simple-lms/client/record-count-view.js';
import { guardDialog, promptDialog } from '@adornis/dialog/dialog-functions.js';
import { ErmValue } from '@adornis/entity-relationship-management/client/details/erm-value.js';
import type { ERMEntityDefinition } from '@adornis/entity-relationship-management/client/types.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { iso639, Language } from '@adornis/internationalization/language-iso639-2.js';
import { html, nothing } from 'lit';
import { createUserForFieldEngineer } from '../../api/create-user-for-field-engineer.js';
import { migrateFieldEngineerEmail } from '../../api/migrate-field-engineer-email.js';
import {
  countScopedFieldEngineers,
  subscribeAllowedPartnerCompanies,
  subscribeScopedFieldEngineersPaginated,
} from '../../api/scoped-queries.js';
import { sendFieldEngineerOnboardingEmail } from '../../api/send-field-engineer-onboarding-email.js';
import { softDeleteFieldEngineer } from '../../api/soft-delete.js';
import { type SmartTeamsCompany, SmartTeamsFieldEngineer } from '../../db/index.js';
import { FEAvailabilitySection } from '../components/fe-availability-section.js';
import { FieldEngineerOnboardingButton } from '../components/onboarding-resend-button.js';
import { PartnerCompanyName } from '../components/partner-company-name.js';
import { ServiceNowEngineerPicker } from '../components/servicenow-pickers.js';
import { formatDateTime } from '../date-format.js';
import { showCreateFieldEngineerDialog } from '../dialogs/index.js';
import { confirmAndSoftDelete } from './soft-delete.js';

// Company picker component for field engineer detail view (multi select)
const CompanyPicker = stateful(
  ({ value, onChange, label }: { value: string[]; onChange: (ids: string[]) => void; label?: string }) => {
    const companiesQuery = useQuery(
      () => subscribeAllowedPartnerCompanies()({ _id: 1, name: 1, address: { zipCity: { zip: 1 } } }),
      [],
    );
    const companies = (companiesQuery.value ?? []) as SmartTeamsCompany[];
    const selectedCompanies = companies.filter(c => value?.includes(c._id));

    if (companiesQuery.loading) {
      return html`<span class="loading loading-spinner loading-sm"></span>`;
    }

    return html`
      <div class="form-control w-full">
        ${label ? html`<label class="label"><span class="label-text">${label}</span></label>` : ''}
        ${DropdownSelectionInput<SmartTeamsCompany, true>({
          multi: true,
          placeholder: 'Pick companies…',
          selectables: companies,
          value: selectedCompanies,
          renderString: company =>
            `${company.name}${company.address?.zipCity?.zip ? ` (${company.address.zipCity.zip})` : ''}`,
          resolveID: company => company._id,
          emptyState: 'No companies available',
          onPicked: value => onChange(((value as SmartTeamsCompany[]) ?? []).map(c => c._id)),
        })}
      </div>
    `;
  },
);

export const fieldEngineerManagerConfig: ERMEntityDefinition<typeof SmartTeamsFieldEngineer> = {
  _class: SmartTeamsFieldEngineer,
  label: 'Field Engineers',
  icon: 'icon-[mdi--account-hard-hat-outline]',
  route: 'field-engineers',
  detail: {
    operation: id => getByID(SmartTeamsFieldEngineer, id),
    selectionSet: {
      _id: 1,
      name: 1,
      email: 1,
      externalId: 1,
      serviceNowSysId: 1,
      partnerCompanyIds: 1,
      isPartner: 1,
      userId: 1,
      mustChangePassword: 1,
      onboardingEmailSentAt: 1,
      firstLanguage: 1,
      secondLanguage: 1,
      archived: 1,
      defaultLocation: { street: 1, number: 1, city: 1, zipCode: 1, country: 1 },
    },
    title: e => e.name ?? 'New Field Engineer',
    sections: [
      {
        title: 'Master data',
        fields: [
          { fieldName: 'name', label: 'Name' },
          {
            fieldName: 'email',
            label: 'Email',
            render: ({ entity }) => {
              if (!entity) return nothing;
              return html`
                <div class="form-control">
                  <label class="label">
                    <span class="label-text">Email</span>
                  </label>
                  <div class="flex items-center gap-2">
                    <input
                      type="email"
                      class="input input-bordered disabled flex-1"
                      value=${entity.email ?? ''}
                      readonly
                    />
                    <button
                      class="btn btn-square btn-outline"
                      @click=${async () => {
                        const newEmail = await promptDialog(
                          html`<p class="text-sm">Migrate email from ${entity.email} to:</p>`,
                          { placeholder: 'New email address', buttonText: 'Migrate' },
                          { modalBoxClasses: 'max-w-sm' },
                        );
                        if (!newEmail) return;

                        await guardDialog(
                          async () => {
                            await migrateFieldEngineerEmail(entity._id, newEmail)();
                            // TODO ERM not reactive yet, pure refetching is not enough
                            window.location.reload();
                          },
                          { successText: 'Email migrated successfully' },
                        );
                      }}
                      title="Migrate email address"
                    >
                      <span class="icon-[mdi--account-arrow-right-outline]"></span>
                    </button>
                  </div>
                </div>
              `;
            },
          },
          { fieldName: 'externalId', label: 'External ID' },
          {
            fieldName: 'serviceNowSysId',
            label: 'ServiceNow Engineer',
            render(props) {
              return ErmValue(
                'ServiceNow Engineer',
                'sync',
                ServiceNowEngineerPicker({ ...props.form.field('serviceNowSysId') }),
              );
            },
          },
          {
            fieldName: 'firstLanguage',
            label: 'First language',
            render(props) {
              return ErmValue(
                'First Language',
                'translate',
                DropdownSelectionInput({
                  ...props.form.field('firstLanguage'),
                  selectables: Object.values(Language),
                  renderString: (language: keyof typeof Language) =>
                    iso639.find(l => l.enumCode === language)?.english ?? language,
                  placeholder: 'e.g. German',
                }),
              );
            },
          },
          {
            fieldName: 'secondLanguage',
            label: 'Second language',
            render(props) {
              return ErmValue(
                'Second Language',
                'translate',
                DropdownSelectionInput({
                  ...props.form.field('secondLanguage'),
                  selectables: Object.values(Language),
                  renderString: (language: keyof typeof Language) =>
                    iso639.find(l => l.enumCode === language)?.english ?? language,
                  placeholder: 'e.g. German',
                }),
              );
            },
          },
          {
            fieldName: 'partnerCompanyIds',
            render: ({ entity }) =>
              CompanyPicker({
                label: 'Company',
                value: entity?.partnerCompanyIds ?? [],
                onChange: companyIds => {
                  if (entity) entity.partnerCompanyIds = companyIds;
                },
              }),
          },
          // Partner or internal. Set by the CSV import ("Engineer Type"); correct it here when ServiceNow was wrong.
          { fieldName: 'isPartner', label: 'Partner engineer' },
        ],
      },
      {
        title: 'Default Location',
        fields: [
          { fieldName: 'defaultLocation.street', label: 'Street' },
          { fieldName: 'defaultLocation.number', label: 'Number' },
          { fieldName: 'defaultLocation.city', label: 'City' },
          { fieldName: 'defaultLocation.zipCode', label: 'ZIP' },
          { fieldName: 'defaultLocation.country', label: 'Country' },
        ],
      },
      {
        title: 'Availabilities',
        render: ({ entity }) => (entity ? FEAvailabilitySection({ fieldEngineer: entity }) : nothing),
      },
      {
        title: 'Login',
        render: ({ entity }) => {
          if (!entity) return nothing;
          if (entity.userId) {
            return html`
              <div class="flex flex-col gap-2">
                <div class="text-sm text-success">Login activated (User ID: ${entity.userId})</div>
                ${entity.mustChangePassword
                  ? html`<div class="text-sm text-warning">⚠️ Password change required</div>`
                  : html`<div class="text-sm text-success">✅ Password change done</div>`}
                ${entity.onboardingEmailSentAt
                  ? html`<div class="text-sm text-base-content/70">
                      📧 Onboarding email last sent: ${formatDateTime(entity.onboardingEmailSentAt)}
                    </div>`
                  : ''}
                <button
                  class="btn btn-outline btn-sm btn-block"
                  @click=${async () => {
                    const password = await promptDialog(
                      html`<p class="text-sm">Enter <b>new</b> password to re-send onboarding email to ${entity.email}:</p>`,
                      { placeholder: 'Password', hidden: true, buttonText: 'Send Email' },
                      { modalBoxClasses: 'max-w-sm' },
                    ).catch(() => null);
                    if (password) {
                      await guardDialog(() => sendFieldEngineerOnboardingEmail(entity._id, password)(), {
                        successText: 'Onboarding email re-sent',
                        errorMessagePrefix: 'Could not re-send onboarding email',
                      });
                    }
                  }}
                >
                  Re-send onboarding email
                </button>
              </div>
            `;
          }
          return html`
            <div class="flex flex-col gap-4">
              <div class="text-sm text-base-content/70">
                ${entity.email
                  ? 'This field engineer has no login. Assign a password to activate login.'
                  : 'Email address required to activate login.'}
              </div>
              ${entity.email
                ? html`
                    <button
                      class="btn btn-primary btn-sm"
                      @click=${async () => {
                        const password = await promptDialog(
                          html`<p class="text-sm">Assign password for ${entity.email}:</p>`,
                          { placeholder: 'Password', hidden: true, buttonText: 'Save' },
                          { modalBoxClasses: 'max-w-sm' },
                        ).catch(() => null);
                        if (password) {
                          await guardDialog(() => createUserForFieldEngineer(entity._id, password)(), {
                            successText: 'Login activated',
                            errorMessagePrefix: 'Could not activate login',
                          });
                        }
                      }}
                    >
                      Activate login
                    </button>
                  `
                : nothing}
            </div>
          `;
        },
      },
    ],
    onDelete: entity =>
      confirmAndSoftDelete({
        entityLabel: 'field engineer',
        name: entity.name,
        run: () => softDeleteFieldEngineer(entity._id)(),
      }),
  },
  list: {
    operation: subscribeScopedFieldEngineersPaginated,
    selectionSet: {
      _id: 1,
      name: 1,
      email: 1,
      partnerCompanyIds: 1,
      archived: 1,
      firstLanguage: 1,
      secondLanguage: 1,
      defaultLocation: { city: 1 },
    },
    columns: [
      {
        fieldName: 'name',
        columnName: 'Name',
        isAlwaysVisible: true,
        selectionSet: { name: 1, email: 1, userId: 1, mustChangePassword: 1, defaultLocation: { city: 1 } },
        renderItem: fe => html`
          <div class="flex flex-col py-2 px-4 min-w-0">
            <div class="flex items-center gap-2">
              <span class="font-medium truncate">${fe.name ?? 'Unnamed'}</span>
              ${fe.userId
                ? html`
                    <span class="tooltip shrink-0" data-tip="Login activated">
                      <span class="badge badge-success badge-xs">●</span>
                    </span>
                    ${fe.mustChangePassword
                      ? html`<span class="tooltip shrink-0" data-tip="Password change required">
                          <span class="badge badge-warning badge-xs">●</span>
                        </span>`
                      : nothing}
                  `
                : nothing}
            </div>
            <span class="text-xs text-neutral-500 truncate">${fe.email ?? ''}</span>
          </div>
        `,
      },
      {
        fieldName: 'companyAndLocation',
        columnName: 'Company & Location',
        isAlwaysVisible: true,
        columnWidth: '160px',
        selectionSet: { partnerCompanyIds: 1, defaultLocation: { city: 1 } },
        renderItem: fe => {
          const ids = fe.partnerCompanyIds ?? [];
          const location = fe.defaultLocation?.city ?? '-';

          if (ids.length === 0 && location === '-') {
            return html`<span class="text-sm text-base-content/50">-</span>`;
          }

          return html`
            <div class="flex flex-col gap-1 w-full py-2 px-4">
              ${ids.length > 0
                ? html`
                    <div class="flex flex-col gap-1 w-full min-w-0">
                      ${ids.map(
                        id => html`
                          <div class="rounded-md border border-base-300 px-2 py-1 w-full min-w-0 overflow-hidden">
                            ${PartnerCompanyName({ companyId: id })}
                          </div>
                        `,
                      )}
                    </div>
                  `
                : nothing}
              ${location !== '-' ? html`<span class="text-xs text-base-content/70">📍 ${location}</span>` : nothing}
            </div>
          `;
        },
      },
    ],
    searchFields: ['name', 'email'],
    listViews: [
      countingTableView(
        filter => countScopedFieldEngineers(filter)(),
        ctx => FieldEngineerOnboardingButton({ filter: ctx.filter }),
      ),
    ],
  },
  onCreate: async () => {
    const newId = await showCreateFieldEngineerDialog();
    return newId ?? '';
  },
};
